import { useEffect, useState } from 'react';
import { adminStatsAPI } from '../../lib/admin-api';

export default function AdminDashboard() {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
    // Auto refresh every 30 seconds
    const interval = setInterval(loadStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadStats = () => {
    adminStatsAPI.getOverview()
      .then(setStats)
      .finally(() => setLoading(false));
  };

  if (loading) return <div className="flex justify-center py-12"><span className="loading loading-spinner loading-lg"></span></div>;

  const totalSubscribers = (stats?.subscriptions?.basic || 0) + (stats?.subscriptions?.premium || 0);
  const onlinePercentage = stats?.users?.total > 0
    ? Math.round((stats?.users?.online / stats?.users?.total) * 100)
    : 0;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Dashboard</h1>
        <button
          className="btn btn-sm btn-ghost"
          onClick={loadStats}
        >
          🔄 Refresh
        </button>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="stat bg-base-100 rounded-box shadow">
          <div className="stat-figure text-primary">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="inline-block w-8 h-8 stroke-current">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
            </svg>
          </div>
          <div className="stat-title">Total Users</div>
          <div className="stat-value text-primary">{stats?.users?.total || 0}</div>
          <div className="stat-desc">
            <span className="text-success">↗︎ {stats?.users?.new_today || 0}</span> new today
          </div>
        </div>

        <div className="stat bg-base-100 rounded-box shadow">
          <div className="stat-figure text-secondary">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="inline-block w-8 h-8 stroke-current">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <div className="stat-title">Online Users</div>
          <div className="stat-value text-secondary">{stats?.users?.online || 0}</div>
          <div className="stat-desc">{onlinePercentage}% of total users</div>
        </div>

        <div className="stat bg-base-100 rounded-box shadow">
          <div className="stat-figure text-accent">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="inline-block w-8 h-8 stroke-current">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
            </svg>
          </div>
          <div className="stat-title">Total Matches</div>
          <div className="stat-value text-accent">{stats?.matches?.total || 0}</div>
          <div className="stat-desc">
            <span className="text-success">↗︎ {stats?.matches?.today || 0}</span> today
          </div>
        </div>

        <div className="stat bg-base-100 rounded-box shadow">
          <div className="stat-figure text-warning">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="inline-block w-8 h-8 stroke-current">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
            </svg>
          </div>
          <div className="stat-title">Pending Reports</div>
          <div className="stat-value text-warning">{stats?.reports?.pending || 0}</div>
          <div className="stat-desc">{stats?.reports?.pending > 0 ? 'Action required' : 'All clear'}</div>
        </div>
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Messages Stats */}
        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title">Messages</h2>
            <div className="stats stats-vertical shadow">
              <div className="stat">
                <div className="stat-title">Total Messages</div>
                <div className="stat-value text-primary">{stats?.messages?.total || 0}</div>
              </div>
              <div className="stat">
                <div className="stat-title">Today</div>
                <div className="stat-value text-secondary">{stats?.messages?.today || 0}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Stats */}
        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title">Subscriptions</h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="badge badge-ghost badge-lg">Free</span>
                <span className="font-bold text-lg">{stats?.subscriptions?.free || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="badge badge-primary badge-lg">Basic</span>
                <span className="font-bold text-lg">{stats?.subscriptions?.basic || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="badge badge-secondary badge-lg">Premium</span>
                <span className="font-bold text-lg">{stats?.subscriptions?.premium || 0}</span>
              </div>
              <div className="divider my-2"></div>
              <div className="flex justify-between items-center">
                <span className="font-semibold">Total Paid</span>
                <span className="font-bold text-xl text-success">{totalSubscribers}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
